import React from 'react';
import {
  getFilteredQuizResultKeys,
  getQuizDisplayName,
  getQuestionForItemProgressQuiz,
  normalizeQuizResultsMap,
} from './itemProgressQuizUtils';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

const CORRECT_COLOR = '#2e7d32';
const INCORRECT_COLOR = '#d32f2f';

const getChoiceAnswerSx = (isSelected: boolean, isAnswerCorrect: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  borderRadius: '5px',
  padding: '5px 8px',
  maxWidth: '632px',
  ...(isAnswerCorrect && !isSelected && {
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
  }),
  ...(isSelected && {
    border: `2px solid ${isAnswerCorrect ? CORRECT_COLOR : INCORRECT_COLOR}`,
    backgroundColor: isAnswerCorrect
      ? 'rgba(46, 125, 50, 0.15)'
      : 'rgba(211, 47, 47, 0.08)',
  }),
});

const ChoiceAnswerOption = ({
  answer,
  isSelected,
  isAnswerCorrect,
}: {
  answer: { id: string; answerText: string };
  isSelected: boolean;
  isAnswerCorrect: boolean;
}) => (
  <Box sx={getChoiceAnswerSx(isSelected, isAnswerCorrect)}>
    <Typography
      sx={{
        color: isAnswerCorrect ? CORRECT_COLOR : INCORRECT_COLOR,
        fontWeight: 'bold',
        flex: 1,
      }}
    >
      {isAnswerCorrect ? '✓ ' : '✗ '}
      {answer.answerText}
    </Typography>
    {isSelected && (
      <Chip
        label="Selected"
        size="small"
        sx={{
          backgroundColor: isAnswerCorrect ? CORRECT_COLOR : INCORRECT_COLOR,
          color: 'white',
        }}
      />
    )}
  </Box>
);

const ChoiceQuestionResult = ({
  typeLabel,
  index,
  questionText,
  isCorrect,
  answers,
  correctAnswer,
  userAnswers,
}: {
  typeLabel: string;
  index: number;
  questionText: string;
  isCorrect: boolean;
  answers: Array<{ id: string; answerText: string }>;
  correctAnswer: string[];
  userAnswers?: string[];
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
      <Typography variant="h6">
        {typeLabel}
        {' '}
        Question
        {' '}
        {index}
        {': '}
        {questionText}
      </Typography>
      <Typography
        sx={{
          color: isCorrect ? CORRECT_COLOR : INCORRECT_COLOR,
          fontWeight: 'bold',
        }}
      >
        {isCorrect ? '✓ Correct' : '✗ Incorrect'}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      ✓/✗ shows the answer key; boxed options are what the user selected.
    </Typography>
    <Stack spacing={1} direction="column">
      {answers.map((answer) => {
        const isSelected = userAnswers?.includes(answer.id) ?? false;
        const isAnswerCorrect = correctAnswer?.includes(answer.id) ?? false;

        return (
          <ChoiceAnswerOption
            key={answer.id}
            answer={answer}
            isSelected={isSelected}
            isAnswerCorrect={isAnswerCorrect}
          />
        );
      })}
    </Stack>
  </Box>
);

const ShowQuestionWithAnswers = ({
  question,
  index,
  isCorrect,
  userAnswers,
  inputValue,
  }:any) => (
    <>
      {question?.type === 'multipleChoiceQuestion' && (
        <ChoiceQuestionResult
          typeLabel="[Multiple Choice]"
          index={index}
          questionText={question.questionText}
          isCorrect={isCorrect}
          answers={question.answers}
          correctAnswer={question.correctAnswer}
          userAnswers={userAnswers}
        />
      )}
      {question?.type === 'multipleResponseQuestion' && (
        <ChoiceQuestionResult
          typeLabel="[Multi Select]"
          index={index}
          questionText={question.questionText}
          isCorrect={isCorrect}
          answers={question.answers}
          correctAnswer={question.correctAnswer}
          userAnswers={userAnswers}
        />
      )}
      {question?.type === 'fillInTheBlank' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">
            [Fill in the Blank] Question
            {' '}
            {index}
            {': '}
            {question.questionText}
          </Typography>
          <Stack spacing={1} direction="column">
            <Typography sx={{ color: isCorrect ? 'green' : 'red', fontWeight: 'bold' }}>
              Answer:
              {' '}
              {inputValue || 'No answer provided'}
              {isCorrect ? ' ✓ ' : ' ✗ '}
            </Typography>
          </Stack>
        </Box>
      )}
      {question?.type === 'openEnded' && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography variant="h6">
              [Open Ended] Question
              {' '}
              {index}
              {': '}
              {question.questionText}
            </Typography>
            <Typography
              sx={{
                color: isCorrect ? 'green' : 'red',
                fontWeight: 'bold',
                ml: '10px',
              }}
            >
              {isCorrect ? '✓ Correct' : '✗ Incorrect'}
            </Typography>
          </Box>
          <Stack spacing={1} direction="column">
            <Typography sx={{ fontWeight: 'bold' }}>
              Answer:
              {' '}
              {inputValue || 'No answer provided'}
            </Typography>
          </Stack>
        </Box>
      )}
    </>
  );

const QuizHeader = ({
  quizName,
  score,
  isPassed,
  completedAt,
}: any) => (
  <>
    <Typography variant="h3">
      Name of Quiz:
      {' '}
      {quizName || 'Deleted Quiz'}
    </Typography>
    <Typography variant="h3" sx={{ mb: 2 }}>
      Completed At:
      {' '}
      {new Date(completedAt).toLocaleString()}
    </Typography>
    <Typography variant="h4" sx={{ mb: 2 }}>
      Score :
      {' '}
      <span style={{ color: isPassed ? 'green' : 'red' }}>
        {score}
        %
      </span>
      <span>
        {isPassed ? (
          <Chip
            label="Passed"
            sx={{
              ml: '10px',
              backgroundColor: 'green',
            }}
          />
        ) : (
          <Chip
            label="Failed"
            sx={{
              ml: '10px',
              backgroundColor: 'red',
            }}
          />
        )}
      </span>
    </Typography>
  </>
);

const ShowResults = ({
  progressItem,
  results,
  sectionID,
}: any) => (
  results?.map(({
    inputValue,
    isCorrect,
    question: questionId,
    userAnswers,
  }: any, index: number) => {
    let question: any;
    try {
      question = getQuestionForItemProgressQuiz(progressItem, sectionID, questionId);
    } catch {
      question = null;
    }
    if (!question) {
      try {
        const quizResult = normalizeQuizResultsMap(progressItem?.quizResults)?.[sectionID];
        const historyAnswers = quizResult?.history?.[0]?.answers;
        const allAnswers = (historyAnswers?.correct || []).concat(historyAnswers?.incorrect || []);
        question = allAnswers.find((q: any) => q.id === questionId);
      } catch {
        question = null;
      }
    }

    if (!question) {
      return null;
    }

    return (
      <Box key={question?.id || `${index}-${questionId}`}>
        <ShowQuestionWithAnswers
          question={question}
          index={index + 1}
          isCorrect={isCorrect}
          inputValue={inputValue}
          userAnswers={userAnswers}
        />
      </Box>
    );
  })
);

const QuizResultDialog = ({
  open,
  handleClose,
  customer,
  item,
}:any) => (
  <Dialog
    open={open}
    // onClose={handleCancel}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
    sx={{
      width: '100%',
      maxWidth: '100%',
    }}
    PaperProps={{
      sx: {
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        margin: 8,
      },
    }}
  >
    <DialogTitle id="alert-dialog-title">
      Quiz Results for
      {' '}
      {customer.name || customer.email}
    </DialogTitle>
    <DialogContent>
      {item && getFilteredQuizResultKeys(item).map((key) => {
        const quizMap = normalizeQuizResultsMap(item.quizResults);
        const quizResult = quizMap[key];
        if (!quizResult) return null;

        const quizName = getQuizDisplayName(item, key);

        return (
          <React.Fragment key={key}>
            {quizResult.isCompleted ? (
              <QuizHeader
                quizName={quizName}
                score={quizResult?.score ?? 0}
                isPassed={quizResult?.isPassed ?? false}
                completedAt={quizResult?.completedAt}
              />
            ) : (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    my: 2,
                  }}
                >
                  Started But Not Completed
                  {' '}
                  {quizResult.startedAt}
                </Typography>
                <Chip
                  label="Not Completed"
                  sx={{
                    backgroundColor: 'orange',
                  }}
                />
              </>
            )}
            <ShowResults
              sectionID={key}
              results={quizResult.results}
              progressItem={item}
            />
          </React.Fragment>
        );
      })}
    </DialogContent>
    <DialogActions>
      <Button onClick={() => handleClose()}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default QuizResultDialog;
